/**
 * Couche reseau. Vit hors de React : c'est un singleton avec un emetteur
 * d'evenements typé, les stores s'y abonnent.
 *
 * Trois responsabilites :
 *  - garder la socket vivante (reconnexion a backoff, renvoi du sessionToken) ;
 *  - ne rien perdre : ce qui est envoye socket fermee est mis en file ;
 *  - donner l'heure du serveur, pour que tous les onglets affichent le meme
 *    timer. Aucune duree locale n'est jamais utilisee pour un compte a rebours.
 */
import {
  CLIENT_DRAWING_TAG,
  DRAWING_TAG,
  decodeDrawingFrame,
  encodeClientDrawing,
  parseServerMessage,
  serialize,
  type ClientMessage,
  type ServerMessage,
} from '@retusch/shared';
import { useUiStore } from '../store/uiStore.js';

const DEFAULT_URL = 'ws://localhost:9001';
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 8000;
/** Nombre d'aller-retours pour estimer l'offset d'horloge a la connexion. */
const CLOCK_SAMPLES = 3;
/**
 * Battement de coeur : un ping applicatif tant que la socket est ouverte.
 *
 * LE CLIENT NE DIT RIEN PENDANT QU'IL DESSINE, c'est la regle du protocole (un
 * seul message par phase de dessin). Or la chaine de proxys de la prod coupe
 * une socket dont le client n'a rien envoye depuis 60 s : mesure sur
 * ws.retusch.io, une socket muette tombe a 61 s (code 1006), la meme avec un
 * ping toutes les 20 s tient indefiniment. Les pings automatiques du serveur
 * n'y changent rien, ils ne partent qu'a l'approche de son propre delai
 * d'inactivite (120 s), donc trop tard.
 *
 * La coupure tombait en plein dessin, 60 s apres le dernier clic de la manche
 * precedente. Le serveur, qui n'attend pas un joueur deconnecte, fermait
 * aussitot la phase : la retouche en cours n'etait jamais envoyee.
 *
 * Vingt secondes laissent passer deux battements perdus avant la coupure. Le
 * pong nourrit au passage l'estimation d'horloge, qui ne vieillit donc plus.
 */
const HEARTBEAT_MS = 20_000;
const SESSION_STORAGE_KEY = 'retusch:session';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting';

export interface DrawingPayload {
  readonly tag: number;
  readonly slot: number;
  readonly drawing: ArrayBuffer;
}

export interface ConnectionEvents {
  message: ServerMessage;
  drawing: DrawingPayload;
  status: ConnectionStatus;
}

type Listener<K extends keyof ConnectionEvents> = (payload: ConnectionEvents[K]) => void;

function resolveUrl(): string {
  // `import.meta.env` est remplace statiquement au build Vite, mais n'existe pas
  // quand ce module tourne hors bundler (harnais de test, SSR eventuel).
  const configured = import.meta.env?.VITE_WS_URL;
  if (configured !== undefined && configured !== '') return configured;
  if (typeof window === 'undefined') return DEFAULT_URL;

  // Repli deduit de l'origine : fonctionne en localhost comme derriere un
  // reverse proxy TLS, sans rebuild.
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const port = window.location.port === '5173' ? ':9001' : '';
  return `${protocol}//${window.location.hostname}${port}`;
}

/**
 * Meme serveur, en HTTP : c'est la que part le beacon de mesure, qui precede
 * toute WebSocket. Derive de la meme resolution que le jeu, pour qu'une seule
 * variable d'environnement pilote les deux.
 */
export function serverHttpBase(): string {
  return resolveUrl().replace(/\/+$/, '').replace(/^ws/, 'http');
}

/** Deux intentions de join sont-elles la MEME demande ? */
function sameJoin(
  a: Extract<ClientMessage, { type: 'join' }>,
  b: Extract<ClientMessage, { type: 'join' }>,
): boolean {
  return (
    a.nickname === b.nickname &&
    a.code === b.code &&
    a.create === b.create &&
    a.visibility === b.visibility &&
    a.avatar === b.avatar
  );
}

function endpoint(override?: string): string {
  const base = (override ?? resolveUrl()).replace(/\/+$/, '');
  return base.endsWith('/ws') ? base : `${base}/ws`;
}

/**
 * Ou vit le token de session. L'implementation par defaut utilise
 * sessionStorage : un token par onglet, efface a la fermeture : exactement la
 * duree de vie voulue, deux onglets etant deux joueurs.
 *
 * Le point d'injection existe pour que plusieurs connexions puissent coexister
 * dans un meme processus (harnais de test multi-onglets), ce qui est impossible
 * avec un stockage global.
 */
export interface TokenStore {
  get(): string | undefined;
  set(token: string): void;
  clear(): void;
}

export function sessionTokenStore(): TokenStore {
  return {
    get: () => {
      try {
        return window.sessionStorage.getItem(SESSION_STORAGE_KEY) ?? undefined;
      } catch {
        return undefined;
      }
    },
    set: (token) => {
      try {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, token);
      } catch {
        // Navigation privee stricte : on continue, sans reconnexion possible.
      }
    },
    clear: () => {
      try {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // Rien a faire.
      }
    },
  };
}

/** Token en memoire : utile hors navigateur et pour les tests. */
export function memoryTokenStore(): TokenStore {
  let token: string | undefined;
  return {
    get: () => token,
    set: (value) => {
      token = value;
    },
    clear: () => {
      token = undefined;
    },
  };
}

export class Connection {
  private readonly tokens: TokenStore;
  private readonly urlOverride: string | undefined;
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = 'idle';
  private attempt = 0;
  private reconnectTimer = 0;
  private heartbeatTimer = 0;
  private closedByUs = false;

  /** Messages emis pendant que la socket etait fermee. */
  private queue: string[] = [];
  private binaryQueue: ArrayBuffer[] = [];

  private clockOffset = 0;
  private clockSamples: number[] = [];

  /** Derniere intention de join, rejouee automatiquement a la reconnexion. */
  private joinIntent: Extract<ClientMessage, { type: 'join' }> | null = null;
  /**
   * Socket sur laquelle cette intention est DEJA partie.
   *
   * LE SERVEUR REFUSE UN SECOND JOIN sur une meme connexion (« deja dans un
   * salon », un BAD_MESSAGE). Or le client en emettait deux pour un seul clic :
   * `BrowseScreen` joignait avant de naviguer, puis l'effet d'arrivee de
   * `RoomScreen` joignait a son tour, la reponse du premier n'etant pas encore
   * la. En developpement, React Strict Mode rejoue les effets au montage et en
   * ajoutait un troisieme : deux bulles « message refuse par le serveur »
   * apparaissaient a chaque entree dans un salon.
   *
   * Le garde vit ICI et pas dans les ecrans : ils sont plusieurs a vouloir
   * rejoindre, ils ne peuvent pas se coordonner entre eux, et la couche reseau
   * est la seule a savoir ce qui est deja parti. La reconnexion reste
   * possible, c'est la SOCKET qui sert de reference : une nouvelle connexion
   * rejoue toujours l'intention.
   */
  private joinSentOn: WebSocket | null = null;

  private readonly listeners: {
    [K in keyof ConnectionEvents]: Set<Listener<K>>;
  } = { message: new Set(), drawing: new Set(), status: new Set() };

  /**
   * `url` surcharge la resolution normale (VITE_WS_URL puis window.location).
   * Sert aux harnais de test, qui doivent viser un port dedie sans dependre
   * d'une variable de build.
   */
  constructor(tokens: TokenStore = sessionTokenStore(), url?: string) {
    this.tokens = tokens;
    this.urlOverride = url;
  }

  // -------------------------------------------------------------------------
  // Abonnement
  // -------------------------------------------------------------------------

  on<K extends keyof ConnectionEvents>(event: K, listener: Listener<K>): () => void {
    this.listeners[event].add(listener as never);
    return () => {
      this.listeners[event].delete(listener as never);
    };
  }

  private emit<K extends keyof ConnectionEvents>(event: K, payload: ConnectionEvents[K]): void {
    for (const listener of this.listeners[event]) (listener as Listener<K>)(payload);
  }

  // -------------------------------------------------------------------------
  // Horloge
  // -------------------------------------------------------------------------

  /** Heure serveur estimee, en ms epoch. */
  now(): number {
    return Date.now() + this.clockOffset;
  }

  /** Temps restant (ms) avant un `endsAt` serveur. Jamais negatif. */
  remaining(endsAt: number | null): number {
    if (endsAt === null) return 0;
    return Math.max(endsAt - this.now(), 0);
  }

  get offsetMs(): number {
    return this.clockOffset;
  }

  get currentStatus(): ConnectionStatus {
    return this.status;
  }

  private syncClock(): void {
    this.clockSamples = [];
    for (let i = 0; i < CLOCK_SAMPLES; i += 1) {
      window.setTimeout(() => this.sendNow({ type: 'ping', t: Date.now() }), i * 120);
    }
  }

  private onPong(sentAt: number, serverTime: number): void {
    const roundTrip = Date.now() - sentAt;
    // Hypothese NTP classique : le trajet est symetrique.
    this.clockSamples.push(serverTime - (sentAt + roundTrip / 2));
    if (this.clockSamples.length > CLOCK_SAMPLES) this.clockSamples.shift();
    this.clockOffset =
      this.clockSamples.reduce((sum, value) => sum + value, 0) / this.clockSamples.length;
  }

  // -------------------------------------------------------------------------
  // Session
  // -------------------------------------------------------------------------

  private storedToken(): string | undefined {
    return this.tokens.get();
  }

  private storeToken(token: string): void {
    this.tokens.set(token);
  }

  clearSession(): void {
    this.joinIntent = null;
    this.joinSentOn = null;
    this.tokens.clear();
  }

  // -------------------------------------------------------------------------
  // Cycle de vie
  // -------------------------------------------------------------------------

  connect(): void {
    if (this.socket !== null && this.socket.readyState <= WebSocket.OPEN) return;

    this.closedByUs = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(endpoint(this.urlOverride));
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempt = 0;
      this.setStatus('open');
      this.syncClock();
      this.startHeartbeat();

      // Le join doit repartir en premier : tout le reste en depend.
      if (this.joinIntent !== null) {
        this.sendNow(this.withToken(this.joinIntent));
        this.joinSentOn = socket;
      }
      this.flush();
    });

    socket.addEventListener('message', (event) => this.onMessage(event));

    socket.addEventListener('close', () => {
      // Une socket remplacee entre-temps ne doit pas emporter celle qui vit.
      if (this.socket !== socket) return;
      this.stopHeartbeat();
      this.socket = null;
      if (this.closedByUs) {
        this.setStatus('idle');
        return;
      }
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // 'close' suit toujours une erreur : la reconnexion est geree la, pas ici.
    });
  }

  disconnect(): void {
    this.closedByUs = true;
    window.clearTimeout(this.reconnectTimer);
    this.stopHeartbeat();
    this.socket?.close();
    this.socket = null;
    this.setStatus('idle');
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(
      () => this.sendNow({ type: 'ping', t: Date.now() }),
      HEARTBEAT_MS,
    );
  }

  private stopHeartbeat(): void {
    window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = 0;
  }

  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    const delay = Math.min(RECONNECT_MIN_MS * 2 ** this.attempt, RECONNECT_MAX_MS);
    this.attempt += 1;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit('status', status);
  }

  // -------------------------------------------------------------------------
  // Reception
  // -------------------------------------------------------------------------

  private onMessage(event: MessageEvent): void {
    if (event.data instanceof ArrayBuffer) {
      const frame = decodeDrawingFrame(event.data);
      if (frame === null) return;
      this.emit('drawing', frame);
      return;
    }

    const message = parseServerMessage(String(event.data));
    if (message === null) return;

    if (message.type === 'pong') {
      this.onPong(message.t, message.serverTime);
      return;
    }
    if (message.type === 'welcome') {
      this.storeToken(message.sessionToken);
    }
    // L'intention se cale sur le salon OBTENU. Une partie rapide part sans
    // code : rejouee telle quelle a la reconnexion, le serveur refaisait un
    // tirage de table (`findQuickPlay`), et des qu'il en existe plusieurs le
    // joueur pouvait atterrir sur une AUTRE, ou son jeton ne dit rien, en
    // perdant sa place et son score. Revenir, c'est revenir a sa table.
    if (
      message.type === 'room' &&
      this.joinIntent !== null &&
      this.joinIntent.code !== message.room.code
    ) {
      const { nickname, avatar } = this.joinIntent;
      this.joinIntent = {
        type: 'join',
        nickname,
        ...(avatar === undefined ? {} : { avatar }),
        code: message.room.code,
      };
    }

    this.emit('message', message);
  }

  // -------------------------------------------------------------------------
  // Envoi
  // -------------------------------------------------------------------------

  /** Memorise l'intention de join pour pouvoir la rejouer a la reconnexion. */
  join(
    nickname: string,
    options: { code?: string; create?: boolean; visibility?: 'public' | 'private' } = {},
  ): void {
    // L'avatar accompagne le pseudo : ce sont les deux memes preferences, et
    // c'est le seul moment ou le salon peut les apprendre.
    const avatar = useUiStore.getState().avatar;
    const intent: Extract<ClientMessage, { type: 'join' }> = {
      type: 'join',
      nickname,
      ...(avatar === null ? {} : { avatar }),
      ...(options.code === undefined ? {} : { code: options.code }),
      ...(options.create === true ? { create: true } : {}),
      ...(options.create === true && options.visibility !== undefined
        ? { visibility: options.visibility }
        : {}),
    };
    // Meme intention, deja partie sur cette socket : on attend la reponse
    // plutot que d'en envoyer une seconde, que le serveur refuserait.
    const alreadySent =
      this.joinIntent !== null &&
      this.joinSentOn !== null &&
      this.joinSentOn === this.socket &&
      sameJoin(this.joinIntent, intent);

    this.joinIntent = intent;
    if (alreadySent) return;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendNow(this.withToken(intent));
      this.joinSentOn = this.socket;
    } else {
      this.connect();
    }
  }

  /**
   * Reessayer apres un refus.
   *
   * Passe outre le garde ci-dessus : le serveur n'a PAS retenu la tentative
   * precedente (elle a echoue), la meme intention doit donc pouvoir repartir
   * telle quelle. Sans cette porte, le bouton « Reessayer » d'un salon plein
   * n'aurait plus rien envoye.
   */
  rejoin(): void {
    const intent = this.joinIntent;
    if (intent === null) return;
    this.joinSentOn = null;
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.sendNow(this.withToken(intent));
      this.joinSentOn = this.socket;
    } else {
      this.connect();
    }
  }

  send(message: ClientMessage): void {
    const payload = serialize(message);
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(payload);
      return;
    }
    this.queue.push(payload);
    this.connect();
  }

  /**
   * Le dessin rendu. Definitif : il fait avancer la manche.
   *
   * La MANCHE part avec : un envoi lent, parti avant la fin d'une manche et
   * arrive apres, serait sinon compte pour la suivante, et son auteur perdrait
   * son tour (cf. `CLIENT_ENVELOPE_BYTES`).
   */
  sendDrawing(drawing: ArrayBuffer, round: number): void {
    const frame = encodeClientDrawing(CLIENT_DRAWING_TAG.submit, drawing, round);
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(frame);
      return;
    }
    this.binaryQueue.push(frame);
    this.connect();
  }

  /**
   * Apercu du dessin en cours, pour les spectateurs.
   *
   * Jamais mis en file : un apercu perdu est sans importance, le suivant arrive
   * deux secondes plus tard. Le rejouer apres une reconnexion enverrait un etat
   * perime, et pire, il pourrait doubler le dessin rendu.
   */
  sendPreview(drawing: ArrayBuffer, round: number): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(encodeClientDrawing(CLIENT_DRAWING_TAG.preview, drawing, round));
  }

  /**
   * Ajoute le token de session s'il en existe un. Ecrit ainsi plutot qu'avec un
   * spread `sessionToken: undefined` : `exactOptionalPropertyTypes` distingue
   * une propriete absente d'une propriete a undefined.
   */
  private withToken(
    intent: Extract<ClientMessage, { type: 'join' }>,
  ): Extract<ClientMessage, { type: 'join' }> {
    const token = this.storedToken();
    return token === undefined ? intent : { ...intent, sessionToken: token };
  }

  /** Envoi direct sans mise en file : reserve au handshake. */
  private sendNow(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(serialize(message));
  }

  private flush(): void {
    const queued = this.queue;
    const binaries = this.binaryQueue;
    this.queue = [];
    this.binaryQueue = [];
    for (const payload of queued) this.socket?.send(payload);
    for (const payload of binaries) this.socket?.send(payload);
  }
}

export const connection = new Connection();
export { DRAWING_TAG };
